<script lang="ts">
import { computed, type CSSProperties } from 'vue';
import { tooltipProps, type PopoverProps } from 'naive-ui';

/** Long content wraps at this width instead of stretching across the viewport. */
const MAX_WIDTH = 320;

/**
 * The same defaults as props, for the places that take tooltip *props* rather
 * than a component: `n-ellipsis`'s `tooltip`, which measures its text and shows
 * the bubble only when it clipped (the runs grid cells, GaiaRunResultRow).
 */
// `style` reaches the bubble as a fallthrough attribute, so naive's type lacks it.
export const gaiaTooltipProps: PopoverProps & { style: CSSProperties } = {
  style: { maxWidth: `${MAX_WIDTH}px` },
};
</script>

<script setup lang="ts">
const props = defineProps({
  ...tooltipProps,
  /**
   * Long content wraps instead of stretching the bubble across the viewport.
   * px; a caller can raise it or pass 0 to opt out. Naive deprecated its own
   * `max-width` prop for `style`, so it is applied as a style here.
   */
  maxWidth: {
    type: Number,
    default: MAX_WIDTH,
  },
});

const passThrough = computed(() => {
  const { maxWidth, ...rest } = props;
  return rest;
});

/** Merged with any `style` the caller passes, which falls through as well. */
const capStyle = computed<CSSProperties>(() =>
  props.maxWidth > 0 ? { maxWidth: `${props.maxWidth}px` } : {}
);
</script>

<template>
  <!-- The bubble from the Playbooks runs grid: ink text on white, 9px radius,
       9/11 padding, and a hairline ring at the Card border weight ahead of the
       drop shadow, so a white bubble still has an edge over white content. All
       of that is the config provider's `Tooltip` theme (gaia-*.json), so this
       wrapper's job is to be the one name for the control and to carry the
       width cap the theme has no var for. The arrow follows the bubble's colour
       but keeps naive's own soft shadow — the ring is a body box-shadow and
       cannot reach it.

       Not every hint is a tooltip: a terse affordance label on an icon button
       stays a native `title` (GaiaIconButton call sites, GaiaAvatarStack), and
       a panel of arbitrary content anchored to a control is GaiaPopover. This
       is for content worth a themed bubble. -->
  <n-tooltip v-bind="passThrough" :style="capStyle">
    <template #trigger>
      <slot name="trigger" />
    </template>
    <slot />
  </n-tooltip>
</template>
